import {inject} from '@angular/core';
import {WebSocketSubject} from 'rxjs/internal/observable/dom/WebSocketSubject';
import {webSocket, WebSocketSubjectConfig} from 'rxjs/webSocket';
import {catchError, EMPTY, Subscription, tap} from 'rxjs';
import {WEBSOCKET_URL} from '../tokens/websocket-url.token';
import {LocalAuthService} from '@sso/ld-sso-with-oauth2-oidc';

/**
 * Shared connect/reconnect/cleanup logic for the app's `/ws/v2/*` sockets.
 * Subclasses provide the path segment, the error/log label, and what to do
 * with each incoming message.
 */
export abstract class WsConnectorServiceBase<T = unknown> {
  private static readonly RECONNECT_DELAY_MS = 1000;

  protected abstract readonly wsPath: string;
  protected abstract readonly logLabel: string;

  /**
   * Close codes that mean "the session is over, don't reconnect" rather than a
   * transient disconnect. Defaults to none - override to opt in.
   */
  protected isFinalCloseCode(_code: number): boolean {
    return false;
  }

  /** Called once, right after a final close code is recognized. Default no-op. */
  protected onFinalClose(_code: number): void {
  }

  private wsBaseUrl = inject(WEBSOCKET_URL);
  private localAuthService = inject(LocalAuthService);

  private socketSubscription?: Subscription;
  private socket$?: WebSocketSubject<T>;
  private reconnectTimeoutId?: ReturnType<typeof setTimeout>;
  private isConnecting = false;
  private manuallyClosed = true;

  async initSocket() {
    this.manuallyClosed = false;

    if (this.isConnecting || this.socket$) {
      return;
    }

    await this.connect();
  }

  public close() {
    this.manuallyClosed = true;
    this.clearReconnectTimer();
    this.isConnecting = false;
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.socket$?.complete();
    this.socket$ = undefined;
  }

  protected abstract handleMessage(message: T): void;

  private async connect() {
    if (this.manuallyClosed || this.isConnecting || this.socket$) {
      return;
    }

    this.isConnecting = true;

    try {
      const token = await this.localAuthService.getAccessTokenIfValid();
      if (this.manuallyClosed) {
        return;
      }

      const socket$ = this.createSocket({
        url: `${this.wsBaseUrl}/ws/v2/${this.wsPath}?token=${token}`,
        openObserver: {
          next: () => {
            this.isConnecting = false;
          }
        },
        closeObserver: {
          next: (event) => {
            this.handleDisconnect(event?.code);
          }
        }
      });

      this.socket$ = socket$;
      this.socketSubscription = socket$.pipe(
        catchError((err) => {
          console.error(`${this.logLabel} error:`, err);
          this.handleDisconnect();
          return EMPTY;
        }),
        tap((v: T) => this.handleMessage(v)),
      ).subscribe();
    } catch (err) {
      console.error(`${this.logLabel} reconnect failed:`, err);
      this.handleDisconnect();
    } finally {
      if (!this.socket$) {
        this.isConnecting = false;
      }
    }
  }

  private createSocket(config: WebSocketSubjectConfig<T>) {
    return webSocket<T>(config);
  }

  private handleDisconnect(closeCode?: number) {
    const hadSocket = !!this.socket$ || this.isConnecting;

    this.isConnecting = false;
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.socket$ = undefined;

    // A final close code (e.g. the demo feed's 4001 "usage ended") means the session
    // is over server-side - reconnecting would just hammer the handshake forever.
    if (closeCode !== undefined && this.isFinalCloseCode(closeCode)) {
      this.manuallyClosed = true;
      this.clearReconnectTimer();
      this.onFinalClose(closeCode);
      return;
    }

    if (!this.manuallyClosed && hadSocket) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeoutId || this.manuallyClosed) {
      return;
    }

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = undefined;
      this.connect().then();
    }, WsConnectorServiceBase.RECONNECT_DELAY_MS);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = undefined;
    }
  }
}
